import { useEffect, useRef, useState } from 'react'
import { useDebounceCallback } from '@global/hooks/useDebounceCallback'
import { cn } from '@global/utils/tailwind'

type Props = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value'> & {
  value: string
  onChange: (name: string) => void
}

export const EditableInput = ({ value, onChange, ...inputProps }: Props) => {
  const [isFocused, setIsFocused] = useState(false)
  const spanRef = useRef<HTMLSpanElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const [inputWidth, setInputWidth] = useState<number>(0)
  const [internalValue, setInternalValue] = useState(value)

  const debouncedCallback = useDebounceCallback({
    callback: () => {
      onChange(internalValue)
    },
    delay: 500,
  })

  useEffect(() => {
    if (spanRef.current) {
      setInputWidth(spanRef.current.offsetWidth)
    }
  }, [internalValue])

  useEffect(() => {
    setInternalValue(value)
  }, [value])

  const handleBlur = () => {
    setIsFocused(false)
    onChange(internalValue)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      inputRef.current?.blur()
    }
  }

  return (
    <div className="relative inline-block">
      <span ref={spanRef} className="invisible absolute whitespace-pre text-xl font-semibold">
        {internalValue || ' '}
      </span>

      <input
        ref={inputRef}
        type="text"
        value={internalValue}
        onChange={event => {
          setInternalValue(event.target.value)
          debouncedCallback()
        }}
        onFocus={() => {
          setIsFocused(true)
        }}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        className={cn(
          'text-xl font-semibold bg-transparent outline-none transition-all duration-200',
          isFocused
            ? 'border border-[#0092F3] rounded'
            : 'border border-transparent hover:border-gray-300 rounded',
        )}
        style={{ width: inputWidth + 4 }}
        {...inputProps}
      />
    </div>
  )
}
